import { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import Reveal from "./Reveal";

/**
 * PORTFOLIO ITEMS — ganti dengan portfolio asli Anda.
 * Field:
 *  - name    : nama brand/klien
 *  - cat     : kategori (harus match salah satu FILTERS di bawah)
 *  - year    : tahun proyek
 *  - stack   : ringkas stack/integrasi
 *  - img     : URL preview desktop mockup toko
 *  - link    : URL live store (opsional; # kalau tidak ada)
 */
const ITEMS = [
  {
    name: "Kopi Rimba",
    cat: "F&B",
    year: "2025",
    stack: "Shopify · Midtrans · JNE",
    img: "https://images.pexels.com/photos/29795384/pexels-photo-29795384.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "#",
  },
  {
    name: "Serat Denim",
    cat: "Fashion",
    year: "2024",
    stack: "Shopify Plus · Xendit · SiCepat",
    img: "https://images.unsplash.com/photo-1539278383962-a7774385fa02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZmFzaGlvbiUyMGVjb21tZXJjZXxlbnwwfHx8fDE3ODQ3MDczNjV8MA&ixlib=rb-4.1.0&q=85",
    link: "#",
  },
  {
    name: "Batik Nara",
    cat: "Craft",
    year: "2024",
    stack: "Shopify · Duitku · J&T",
    img: "https://images.unsplash.com/photo-1604973104381-870c92f10343?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxpbmRvbmVzaWFuJTIwY3JhZnQlMjBiYXRpayUyMGxpZmVzdHlsZXxlbnwwfHx8fDE3ODQ3MDczNjV8MA&ixlib=rb-4.1.0&q=85",
    link: "#",
  },
  {
    name: "Lumen Skin",
    cat: "Beauty",
    year: "2025",
    stack: "Shopify · Midtrans · AnterAja",
    img: "https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NDU3ODl8MHwxfHNlYXJjaHwyfHxza2luY2FyZSUyMGVkaXRvcmlhbHxlbnwwfHx8fDE3ODQ3MDczNjV8MA&ixlib=rb-4.1.0&q=85",
    link: "#",
  },
  {
    name: "Aksa Living",
    cat: "Home",
    year: "2024",
    stack: "Shopify · Xendit · Ninja",
    img: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1200&q=80",
    link: "#",
  },
  {
    name: "Roa Beans",
    cat: "F&B",
    year: "2023",
    stack: "Shopify · Midtrans · Pos ID",
    img: "https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&w=1200&q=80",
    link: "#",
  },
  {
    name: "Nyala Studio",
    cat: "Fashion",
    year: "2025",
    stack: "Shopify · Xendit · JNE",
    img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1200&q=80",
    link: "#",
  },
  {
    name: "Petal & Bloom",
    cat: "Beauty",
    year: "2024",
    stack: "Shopify · Midtrans · SiCepat",
    img: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=1200&q=80",
    link: "#",
  },
  {
    name: "Kayana Craft",
    cat: "Craft",
    year: "2023",
    stack: "Shopify · Duitku · AnterAja",
    img: "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&w=1200&q=80",
    link: "#",
  },
];

const FILTERS = ["All", "Fashion", "F&B", "Beauty", "Craft", "Home"];

// Split a string into single-character spans for the vertical letter-stack
function splitLetters(text) {
  return text.split("").map((c, i) => ({ c: c === " " ? "\u00A0" : c, i }));
}

export default function Portfolio({ t }) {
  const [filter, setFilter] = useState("All");
  const headingRef = useRef(null);
  const headingInView = useInView(headingRef, { once: true, margin: "-10%" });

  const items = useMemo(
    () => (filter === "All" ? ITEMS : ITEMS.filter((x) => x.cat === filter)),
    [filter]
  );

  const heading = "PORTFOLIO";
  const letters = splitLetters(heading);

  return (
    <section
      id="work"
      className="relative pt-24 md:pt-32 pb-20 md:pb-28 border-t border-[var(--border)] overflow-hidden"
    >
      {/* Overline */}
      <div className="container-x">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <Reveal>
              <div className="flex items-center gap-3 text-[10px] md:text-xs uppercase tracking-[0.3em] text-[var(--text-muted)] font-display">
                <span className="w-6 h-[1px] bg-[var(--accent)]" />
                {t.chapter02?.overline || "Selected Work"}
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="mt-5 text-[var(--text)] font-serif-italic text-xl md:text-2xl max-w-md leading-snug">
                {t.chapter02?.subtitle}
              </p>
            </Reveal>
          </div>

          {/* Filters */}
          <div className="col-span-12 md:col-span-8 flex md:justify-end items-start md:items-end">
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`portfolio-filter-${f.toLowerCase().replace(/\W/g, "")}`}
                    className={`px-4 py-2 rounded-full border text-xs font-display uppercase tracking-widest transition-all ${
                      filter === f
                        ? "bg-[var(--text)] text-[var(--bg)] border-[var(--text)]"
                        : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--text)]"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Kinetic vertical letter-stack heading */}
      <div className="relative mt-10 md:mt-14">
        <div className="container-x grid grid-cols-12 gap-6 md:gap-10 items-start">
          {/* Left column: vertical letter stack (Agenci-style) */}
          <div
            ref={headingRef}
            className="col-span-3 md:col-span-2 lg:col-span-2 xl:col-span-2"
          >
            <div className="flex flex-col items-start leading-[0.82]">
              {letters.map(({ c, i }) => (
                <span key={i} className="mask-line">
                  <motion.span
                    initial={{ y: "110%", opacity: 0 }}
                    animate={
                      headingInView
                        ? { y: "0%", opacity: 1 }
                        : { y: "110%", opacity: 0 }
                    }
                    transition={{
                      duration: 0.75,
                      delay: i * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="inline-block font-display font-black uppercase tracking-tighter text-[15vw] md:text-[8vw] lg:text-[7vw] text-[var(--text)]"
                  >
                    {c}
                  </motion.span>
                </span>
              ))}
              {/* Italic tail */}
              <span className="mask-line mt-2">
                <motion.span
                  initial={{ y: "110%", opacity: 0 }}
                  animate={
                    headingInView
                      ? { y: "0%", opacity: 1 }
                      : { y: "110%", opacity: 0 }
                  }
                  transition={{
                    duration: 0.75,
                    delay: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="inline-block font-serif-italic text-[var(--accent)] text-[10vw] md:text-[5vw] lg:text-[3.5vw] leading-none normal-case"
                >
                  demos.
                </motion.span>
              </span>
            </div>
          </div>

          {/* Right column: grid of portfolio items */}
          <div className="col-span-9 md:col-span-10 lg:col-span-10 xl:col-span-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
              <AnimatePresence mode="popLayout">
                {items.map((item, i) => (
                  <motion.a
                    layout
                    key={item.name}
                    href={item.link}
                    target={item.link !== "#" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`portfolio-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      duration: 0.6,
                      delay: (i % 6) * 0.06,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="group block"
                  >
                    {/* Thumbnail with mockup shadow feel */}
                    <div className="photo-spot relative rounded-xl overflow-hidden border border-[var(--border)] aspect-[4/3] bg-[#0d0d0d]">
                      <img
                        src={item.img}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Category pill */}
                      <div className="absolute z-10 top-4 left-4 flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest font-display bg-black/50 backdrop-blur text-[var(--text)] px-3 py-1 rounded-full border border-[var(--border)]">
                          {item.cat}
                        </span>
                      </div>
                      {/* Number badge */}
                      <div className="absolute z-10 top-4 right-4 text-[10px] uppercase tracking-widest font-display bg-black/50 backdrop-blur text-[var(--text-muted)] px-3 py-1 rounded-full border border-[var(--border)]">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 z-10 flex items-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="w-full flex items-center justify-between">
                          <span className="text-[var(--text)] font-display uppercase tracking-widest text-xs">
                            Preview
                          </span>
                          <span className="w-10 h-10 rounded-full bg-[var(--accent)] text-[var(--bg)] flex items-center justify-center text-lg">
                            ↗
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Meta below */}
                    <div className="mt-4 flex items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display font-black uppercase tracking-tight text-lg md:text-xl text-[var(--text)] truncate group-hover:text-[var(--accent)] transition-colors">
                          {item.name}
                        </div>
                        <div className="mt-0.5 text-xs text-[var(--text-muted)] truncate">
                          {item.stack}
                        </div>
                      </div>
                      <div className="font-display text-xs text-[var(--text-muted)] uppercase tracking-widest shrink-0">
                        {item.year}
                      </div>
                    </div>
                  </motion.a>
                ))}
              </AnimatePresence>
            </div>

            {/* Empty state */}
            {items.length === 0 && (
              <div className="text-[var(--text-muted)] font-serif-italic text-lg mt-8">
                Belum ada karya di kategori ini — coba filter lain.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
